import { useEffect, useState, useCallback } from "react";
import { ClockIcon, UserGroupIcon, UserIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import { fetchMyPanels, fetchJuryIdeas, fetchIdeaSelections } from "../../api/jury";
import ReviewDecisionModal, { ReviewIdea } from "./ReviewDecisionModal";
 
interface PanelMember { _id: string; name: string; email: string; }
interface Panel {
  _id: string;
  name: string;
  scope: "idea_selection" | "round";
  roundType?: string | null;
  members: PanelMember[];
  hackathonId: {
    _id: string;
    title: string;
    phases: { submission: { ideaSelection: { decisionDeadline: string } } };
  };
}
 
const fmtDeadline = (d: string) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
 
const daysLeft = (d: string) =>
  Math.max(0, Math.ceil((new Date(d).getTime() - Date.now()) / 86400000));
 
const initials = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
 
const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pending:  { label: "PENDING REVIEW", cls: "bg-orange-100 text-orange-600" },
  selected: { label: "SELECTED",       cls: "bg-teal-100 text-teal-700"    },
  rejected: { label: "REJECTED",       cls: "bg-red-100 text-red-600"      },
};
 
export default function JuryDashboard() {
  const { token } = useAuth();
 
  const [panel, setPanel]         = useState<Panel | null>(null);
  const [ideas, setIdeas]         = useState<ReviewIdea[]>([]);
  const [decisions, setDecisions] = useState<Record<string, "selected" | "rejected">>({});
  const [ratings, setRatings]     = useState<Record<string, number>>({});
  const [loading, setLoading]     = useState(true);
  const [notAssigned, setNotAssigned] = useState(false);
  const [selectedSection, setSelectedSection] = useState<"to-review" | "reviewed">("to-review");
  
  const [selectedIdea, setSelectedIdea] = useState<ReviewIdea | null>(null);
 
  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const panels: Panel[] = await fetchMyPanels(token);
      // Prefer idea_selection panel for review queue; fall back to first panel
      const p = panels.find((x) => x.scope === "idea_selection") ?? panels[0];
      setPanel(p);
      const hackathonId = p.hackathonId._id;
      const [rawIdeas, rawSelections] = await Promise.all([
        fetchJuryIdeas(token, hackathonId),
        fetchIdeaSelections(token, hackathonId),
      ]);
      const filteredIdeas = rawIdeas.filter((i: any) => ["submitted_final", "selected", "rejected"].includes(i.status));
      setIdeas(filteredIdeas);
      
      const map: Record<string, "selected" | "rejected"> = {};
      const ratingMap: Record<string, number> = {};
      // Seed decisions from idea status first (covers ideas decided before this jury logged in)
      filteredIdeas.forEach((i: any) => {
        if (i.status === "selected" || i.status === "rejected") map[String(i._id)] = i.status;
      });
      // Override/add with IdeaSelection records (more authoritative, includes rating)
      // Seed decisions from idea status first (covers ideas decided before this jury logged in)
      filteredIdeas.forEach((i: any) => {
        if (i.status === "selected" || i.status === "rejected") map[String(i._id)] = i.status;
      });
      // Override/add with IdeaSelection records (more authoritative, includes rating)
      rawSelections.forEach((s: any) => {
        const id = String(s.ideaId?._id ?? s.ideaId);
        
        const decision = s.decision || s.ideaId?.status;
        if (decision === "selected" || decision === "rejected") map[id] = decision;
        if (s.rating) ratingMap[id] = s.rating;
      });
      setDecisions(map);
      setRatings(ratingMap);
    } catch {
      setNotAssigned(true);
    } finally {
      setLoading(false);
    }
  }, [token]);
 
  useEffect(() => { load(); }, [load]);
 
  const handleDecisionSubmit = (ideaId: string, decision: "selected" | "rejected", rating?: number) => {
    setDecisions((prev) => ({ ...prev, [ideaId]: decision }));
    if (rating) setRatings((prev) => ({ ...prev, [ideaId]: rating }));
  };
 
  const deadline = panel?.hackathonId?.phases?.submission?.ideaSelection?.decisionDeadline;
 
  const enriched = ideas.map((idea) => ({
    ...idea,
    decisionStatus: (decisions[idea._id] ?? "pending") as "pending" | "selected" | "rejected",
  }));

  const toReview = enriched.filter(i => i.decisionStatus === 'pending');
  const reviewed = enriched.filter(i => i.decisionStatus !== 'pending');

  const existingDecision = selectedIdea ? (decisions[selectedIdea._id] ?? null) : null;
  const existingRating = selectedIdea ? (ratings[selectedIdea._id] ?? 0) : 0;
 
  // NOT ASSIGNED
  if (!loading && notAssigned) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-sky-50 to-white flex items-center justify-center p-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center max-w-md">
          <p className="text-base font-semibold text-amber-800">You are not assigned to any panel.</p>
          <p className="text-sm text-amber-600 mt-2">Contact the admin to be added to an idea selection panel.</p>
        </div>
      </div>
    );
  }
 
  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-sky-50 to-white">
        <div className="mx-auto max-w-5xl px-3 sm:px-4 md:px-6 py-4 sm:py-6">

          {/* HEADER */}
          <div className="mb-4 sm:mb-6">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Review Queue</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Review and make final decisions on submitted ideas</p>
          </div>
 
          {loading ? (
            <div className="space-y-3 sm:space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-lg sm:rounded-2xl bg-white p-4 sm:p-6 shadow-sm animate-pulse h-24 sm:h-28" />
              ))}
            </div>
          ) : (
            <>
              {/* FILTERS BAR */}
              <div className="mb-4 sm:mb-6 rounded-lg sm:rounded-2xl bg-white px-4 sm:px-6 py-3 sm:py-4 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Assigned Hackathon</p>
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 break-words">{panel?.hackathonId.title ?? "—"}</p>
                </div>
              </div>
 
              {/* DEADLINE BANNER */}
              {deadline && (
                <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 rounded-lg sm:rounded-2xl bg-orange-50 px-4 sm:px-6 py-4">
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1">
                    <div className="flex h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-full bg-orange-100">
                      <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-900">Decision Deadline Approaching</p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        All decisions must be submitted by {fmtDeadline(deadline)}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-orange-600 flex-shrink-0">
                    {daysLeft(deadline)} days left
                  </span>
                </div>
              )}

              {/* SECTION TABS */}
              <div className="mb-4 sm:mb-6 border-b border-gray-200">
                <div className="flex gap-4 sm:gap-8 overflow-x-auto">
                  <button
                    onClick={() => setSelectedSection("to-review")}
                    className={`pb-2 sm:pb-3 px-0.5 sm:px-1 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                      selectedSection === "to-review"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Ideas to Review ({toReview.length})
                  </button>
                  <button
                    onClick={() => setSelectedSection("reviewed")}
                    className={`pb-2 sm:pb-3 px-0.5 sm:px-1 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                      selectedSection === "reviewed"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Reviewed Ideas ({reviewed.length})
                  </button>
                </div>
              </div>

              {/* REVIEW LIST */}
              <div>
                {selectedSection === "to-review" ? (
                  // TO REVIEW SECTION
                  <div>
                    <div className="space-y-3 sm:space-y-4">
                      {toReview.length === 0 ? (
                        <p className="text-center text-xs sm:text-sm text-gray-400 py-6 sm:py-8">No ideas pending review.</p>
                      ) : (
                        toReview.map((item) => {
                          const team = typeof item.teamId === "object" && item.teamId !== null ? item.teamId as any : null;
                          const teamName = team?.teamName ?? "—";
                          const leaderName = team?.leader?.name ?? "—";
                          const submittedAt = item.submittedFinalAt
                            ? new Date(item.submittedFinalAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—";

                          return (
                            <div key={item._id} className="rounded-lg sm:rounded-2xl bg-white p-4 sm:p-6 shadow-sm">
                              <div className="mb-3 sm:mb-2 flex flex-wrap items-center gap-2">
                                <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-600">
                                  PENDING REVIEW
                                </span>
                              </div>
                              <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-6">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-xs sm:text-sm font-semibold text-gray-900 break-words">{item.title}</h4>
                                  <p className="mt-1 text-xs sm:text-sm text-gray-600 line-clamp-2">{item.problem}</p>
                                  <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs text-gray-500">
                                    <span className="flex items-center gap-1"><UserGroupIcon className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{teamName}</span></span>
                                    <span className="flex items-center gap-1"><UserIcon className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{leaderName}</span></span>
                                    <span className="flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{submittedAt}</span></span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => setSelectedIdea(item)}
                                  className="w-full sm:w-auto h-fit rounded bg-blue-600 px-4 py-2 text-xs sm:text-sm font-medium text-white hover:bg-blue-700 whitespace-nowrap transition-colors"
                                >
                                  Review
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ) : (
                  // REVIEWED SECTION
                  <div>
                    <div className="space-y-3 sm:space-y-4">
                      {reviewed.length === 0 ? (
                        <p className="text-center text-xs sm:text-sm text-gray-400 py-6 sm:py-8">No reviewed ideas yet.</p>
                      ) : (
                        reviewed.map((item) => {
                          const badge = STATUS_BADGE[item.decisionStatus] ?? STATUS_BADGE.pending;
                          const team = typeof item.teamId === "object" && item.teamId !== null ? item.teamId as any : null;
                          const teamName = team?.teamName ?? "—";
                          const leaderName = team?.leader?.name ?? "—";
                          const submittedAt = item.submittedFinalAt
                            ? new Date(item.submittedFinalAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—";

                          return (
                            <div key={item._id} className="rounded-lg sm:rounded-2xl bg-white p-4 sm:p-6 shadow-sm">
                              <div className="mb-3 sm:mb-2 flex flex-wrap items-center gap-2">
                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>
                                  {badge.label}
                                </span>
                              </div>
                              <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-6">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-xs sm:text-sm font-semibold text-gray-900 break-words">{item.title}</h4>
                                  <p className="mt-1 text-xs sm:text-sm text-gray-600 line-clamp-2">{item.problem}</p>
                                  <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs text-gray-500">
                                    <span className="flex items-center gap-1"><UserGroupIcon className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{teamName}</span></span>
                                    <span className="flex items-center gap-1"><UserIcon className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{leaderName}</span></span>
                                    <span className="flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{submittedAt}</span></span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => setSelectedIdea(item)}
                                  className="h-fit rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 whitespace-nowrap transition-colors"
                                >
                                  View
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
 
      <ReviewDecisionModal
        open={selectedIdea !== null}
        idea={selectedIdea}
        panelId={panel?._id ?? ""}
        existingDecision={existingDecision as "selected" | "rejected" | null}
        onClose={() => setSelectedIdea(null)}
        onDecisionSubmit={handleDecisionSubmit}
        sidebarWidth={230}
      />
    </>
  );
}
 